// src/pages/CancionesWebList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where
} from "firebase/firestore";
import { db } from "../firebaseConfig";

import "./CancionesWebList.css";

const PAGE_SIZE = 100;

// ✅ Sin i18n
const STATUS_OPTIONS = [
  { key: "pending", label: "Pendientes" },
  { key: "ready", label: "Listas" },
  { key: "complete", label: "Completadas" },
  { key: "all", label: "Todas" }
];

const ORIGEN_OPTIONS = [
  { key: "all", label: "Todas" },
  { key: "web", label: "Web" },
  { key: "app", label: "App" }
];

function formatTs(ts) {
  try {
    if (!ts) return "—";
    const d = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
    return d.toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "—";
  }
}

function statusLabel(st) {
  const key = String(st || "").toLowerCase();
  const opt = STATUS_OPTIONS.find((o) => o.key === key);
  return opt ? opt.label : (key || "—");
}

function getEffectiveTs(it) {
  return (
    it?.updatedAt ||
    it?.shareUpdatedAt ||
    it?.createdAt ||
    it?.fechaCreacion ||
    null
  );
}

function tsToMillis(ts) {
  if (!ts) return null;
  try {
    const d = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
    const t = d.getTime();
    return Number.isFinite(t) ? t : null;
  } catch {
    return null;
  }
}

export default function CancionesWebList() {
  const navigate = useNavigate();

  const [status, setStatus] = useState("pending"); // ✅ default pending
  const [origen, setOrigen] = useState("all");     // all | web | app

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [items, setItems] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const headerLabel = useMemo(() => {
    const opt = STATUS_OPTIONS.find((x) => x.key === status);
    return opt ? opt.label : status;
  }, [status]);

  const origenLabel = useMemo(() => {
    const opt = ORIGEN_OPTIONS.find((x) => x.key === origen);
    return opt ? opt.label : origen;
  }, [origen]);

  /**
   * ✅ Query:
   * - status: se filtra en Firestore (si no es "all")
   * - origen:
   *    - "web": se filtra en Firestore con where(desdeWeb == true)
   *    - "app": NO se puede filtrar perfectamente en Firestore si
   *             quieres incluir null / undefined / no existe.
   *             (Firestore no tiene "field does not exist")
   *             Entonces "app" lo filtramos en FRONT.
   */
  const buildQuery = ({ afterDoc } = {}) => {
    const base = collection(db, "cancionesWeb");
    const clauses = [];

    // ✅ "all" significa NO filtrar por status
    if (status !== "all") clauses.push(where("status", "==", status));

    // ✅ Filtrado en Firestore SOLO para Web (reduce carga)
    if (origen === "web") clauses.push(where("desdeWeb", "==", true));

    // ✅ Orden general (recomendado): updatedAt
    clauses.push(orderBy("updatedAt", "desc"));

    // startAfter debe ir antes del limit
    if (afterDoc) clauses.push(startAfter(afterDoc));

    clauses.push(limit(PAGE_SIZE));
    return query(base, ...clauses);
  };

  const loadFirstPage = async () => {
    setLoading(true);
    setError("");
    setItems([]);
    setLastDoc(null);
    setHasMore(false);

    try {
      const q = buildQuery();
      const snap = await getDocs(q);

      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setItems(rows);

      const last = snap.docs[snap.docs.length - 1] || null;
      setLastDoc(last);

      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (e) {
      console.error(e);
      setError("Error al cargar.");
    } finally {
      setLoading(false);
    }
  };

  const loadNextPage = async () => {
    if (!lastDoc || loading) return;

    setLoading(true);
    setError("");

    try {
      const q = buildQuery({ afterDoc: lastDoc });
      const snap = await getDocs(q);

      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setItems((prev) => [...prev, ...rows]);

      const last = snap.docs[snap.docs.length - 1] || null;
      setLastDoc(last);

      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (e) {
      console.error(e);
      setError("Error al cargar.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Filtrado FRONT para "app" (false/null/undefined/no existe)
  const filteredItems = useMemo(() => {
    if (origen === "all") return items;
    if (origen === "web") return items.filter((x) => x?.desdeWeb === true);
    // origen === "app": todo lo que NO sea true => app
    return items.filter((x) => x?.desdeWeb !== true);
  }, [items, origen]);

  // ✅ Separar por fecha efectiva + ordenar visualmente
  const { withDate, noDate } = useMemo(() => {
    const arr = filteredItems || [];
    const withDate = [];
    const noDate = [];

    for (const it of arr) {
      const ts = getEffectiveTs(it);
      if (ts) withDate.push(it);
      else noDate.push(it);
    }

    withDate.sort((a, b) => {
      const ta = tsToMillis(getEffectiveTs(a)) ?? -Infinity;
      const tb = tsToMillis(getEffectiveTs(b)) ?? -Infinity;
      return tb - ta;
    });

    return { withDate, noDate };
  }, [filteredItems]);

  useEffect(() => {
    loadFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, origen]);

  return (
    <div className="page">
      <div className="container">
        <div className="cwl-header">
          <div>
            <h1 className="h1">Canciones</h1>
            <p className="p-muted" style={{ marginTop: 6 }}>
              Mostrando: <strong>{headerLabel}</strong> | Origen:{" "}
              <strong>{origenLabel}</strong>
            </p>
          </div>

          <div className="cwl-filters">
            <select
              className="cwl-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>

            <select
              className="cwl-select"
              value={origen}
              onChange={(e) => setOrigen(e.target.value)}
            >
              {ORIGEN_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>

            <button className="btn" onClick={loadFirstPage} disabled={loading}>
              Actualizar
            </button>
          </div>
        </div>

        {error && <div className="alert-error">{error}</div>}

        <div className="card cwl-card">
          <div className="cwl-tableHead">
            <div>songId</div>
            <div>usuarioId</div>
            <div>título</div>
            <div>origen</div>
            <div>status</div>
            <div>updated</div>
          </div>

          {loading && items.length === 0 ? (
            <div className="cwl-loading">
              <div className="spinner" />
              <span>Cargando…</span>
            </div>
          ) : null}

          {!loading && filteredItems.length === 0 ? (
            <div className="cwl-empty">No hay registros.</div>
          ) : null}

          {/* ✅ Con fecha (ordenados por fecha efectiva) */}
          {withDate.map((it) => {
            const title = (it.shareTitle || it.titulo || "").trim() || "—";

            const stKey = String(it.status || "pending").toLowerCase();
            // ✅ Si algún doc trae "all" como status, lo tratamos como pending (o cámbialo a "—" si prefieres)
            const safeStKey = stKey === "all" ? "pending" : stKey;

            const desdeWeb = it?.desdeWeb === true; // ✅ null/undefined => false
            const origenText = desdeWeb ? "Web" : "App";

            const effectiveTs = getEffectiveTs(it);

            return (
              <button
                key={it.id}
                type="button"
                className="cwl-row"
                onClick={() => navigate(`/canciones-web/${it.id}`)}
              >
                <div className="cwl-mono">{it.songId || it.id}</div>
                <div className="cwl-mono">{it.usuarioId || "—"}</div>
                <div className="cwl-title">{title}</div>

                <div className={`cwl-badge ${desdeWeb ? "cwl-web" : "cwl-app"}`}>
                  {origenText}
                </div>

                <div className={`cwl-badge cwl-${safeStKey}`}>
                  {statusLabel(safeStKey)}
                </div>

                <div>{formatTs(effectiveTs)}</div>
              </button>
            );
          })}

          {/* ✅ Sin fecha */}
          {noDate.length > 0 ? (
            <div className="cwl-subsection">
              <div className="cwl-subtitle">Sin fecha de registro</div>

              {noDate.map((it) => {
                const title = (it.shareTitle || it.titulo || "").trim() || "—";

                const stKey = String(it.status || "pending").toLowerCase();
                const safeStKey = stKey === "all" ? "pending" : stKey;

                const desdeWeb = it?.desdeWeb === true;
                const origenText = desdeWeb ? "Web" : "App";

                return (
                  <button
                    key={it.id}
                    type="button"
                    className="cwl-row"
                    onClick={() => navigate(`/canciones-web/${it.id}`)}
                  >
                    <div className="cwl-mono">{it.songId || it.id}</div>
                    <div className="cwl-mono">{it.usuarioId || "—"}</div>
                    <div className="cwl-title">{title}</div>

                    <div className={`cwl-badge ${desdeWeb ? "cwl-web" : "cwl-app"}`}>
                      {origenText}
                    </div>

                    <div className={`cwl-badge cwl-${safeStKey}`}>
                      {statusLabel(safeStKey)}
                    </div>

                    <div>—</div>
                  </button>
                );
              })}
            </div>
          ) : null}

          <div className="cwl-footer">
            <div className="p-muted">
              Total: {filteredItems.length}
              <span style={{ marginLeft: 8 }}>
                (Con fecha: {withDate.length} | Sin fecha: {noDate.length})
              </span>
              {origen === "app" ? (
                <span style={{ marginLeft: 8 }}>(filtrado en frontend)</span>
              ) : null}
            </div>

            <button
              className="btn btn-primary"
              onClick={loadNextPage}
              disabled={loading || !hasMore}
              title={!hasMore ? "No hay más" : ""}
            >
              {loading ? "Cargando…" : "Siguiente"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}